/**
 * Reusable empty state component for dashboard tabs.
 * Shows contextual messaging with optional CTA based on the active tab.
 */

import {
  BarChart3,
  Shield,
  Lightbulb,
  FileSpreadsheet,
  LayoutList,
} from 'lucide-react';
import type { ReactNode } from 'react';

import { Button } from '@/components/ui/Button';
import type { DashboardTab } from '@/types/dashboard';

// ============================================================================
// TYPES
// ============================================================================

export interface EmptyStateProps {
  tab?: DashboardTab;
  title?: string;
  description?: string;
  message?: string;
  actionLabel?: string;
  onAction?: () => void;
  className?: string;
}

// ============================================================================
// DEFAULT MESSAGES
// ============================================================================

const DEFAULT_MESSAGES: Record<DashboardTab, string> = {
  verdicts:
    'No creatives yet. Import your first creative to see verdict distribution.',
  gates:
    'No gate data available. Analyze creatives to see quality gate pass rates.',
  insights:
    'Not enough data for insights. Analyze more creatives to unlock KPI drivers and composition views.',
  creatives:
    'No creatives match the current filters. Try adjusting your filters or import new creatives.',
  reports:
    'No saved reports yet. Create a custom report to explore your data.',
};

const TAB_ICONS: Record<DashboardTab, ReactNode> = {
  verdicts: <BarChart3 className="w-10 h-10" />,
  gates: <Shield className="w-10 h-10" />,
  insights: <Lightbulb className="w-10 h-10" />,
  creatives: <LayoutList className="w-10 h-10" />,
  reports: <FileSpreadsheet className="w-10 h-10" />,
};

// ============================================================================
// COMPONENT
// ============================================================================

export function EmptyState({
  tab,
  title,
  description,
  message,
  actionLabel,
  onAction,
  className,
}: EmptyStateProps) {
  const displayMessage = message ?? description ?? (tab ? DEFAULT_MESSAGES[tab] : 'No data available.');
  return (
    <div
      className={`flex flex-col items-center justify-center py-16 gap-3 rounded-xl border border-dashed border-border-subtle bg-surface ${className ?? ''}`}
    >
      <div className="text-muted-foreground">{tab ? TAB_ICONS[tab] : null}</div>
      {title && <h3 className="text-sm font-medium text-primary">{title}</h3>}
      <p className="text-sm text-secondary max-w-md text-center">
        {displayMessage}
      </p>
      {actionLabel && onAction && (
        <Button variant="primary" size="sm" onClick={onAction} className="mt-2">
          {actionLabel}
        </Button>
      )}
    </div>
  );
}
